import { useRouteMatcher, useRoutes } from '@folklore/routes';
import queryString from 'query-string';
import { startTransition, useEffect, useReducer, useRef } from 'react';
import { useLocation, useSearch } from 'wouter';
import { navigate as browserNavigate } from 'wouter/use-browser-location';

import {
    createTimelineState,
    getCurrentEntry,
    getPathname,
    hasNextPlaceholder,
    isSamePageHistoryState,
    timelineReducer,
    type TimelineAction,
    type TimelineEntry,
} from '../lib/timeline';
import { getTrackingQuery } from '../lib/utils';

import { useRequestBase } from '../contexts/RequestContext';
import { useSkipTimeline, useTimelineNext } from './useTimeline';
import { useDocumentUrlGenerator } from './useUrlGenerator';

// Routes whose sub-pages stay on the same timeline entry
const samePageRoutes = [
    'map.wildcard',
    'account.wildcard',
    'auth.wildcard',
    'subscription.wildcard',
    'section.type',
    'pro',
];

function getSpecificPart(url: string): string {
    const parts = getPathname(url)
        .replace(/^\/?(.*)\/?$/, '$1')
        .split('/');
    return parts
        .filter(
            (it) => parts.length <= 1 || it.match(/^quatre95|dehors|periode-libre|mollo$/) === null,
        )
        .slice(0, 2)
        .join('/');
}

function getBrowserUrl(): string {
    return `${window.location.pathname}${window.location.search}`;
}

interface UseTimelineNavigationOptions {
    firstLoadNextDelay?: number;
    loadNextDelay?: number;
    withoutNext?: boolean;
}

function useTimelineNavigation({
    firstLoadNextDelay = 2000,
    loadNextDelay = 1000,
    withoutNext = false,
}: UseTimelineNavigationOptions = {}) {
    const [pathname] = useLocation();
    const search = useSearch();
    const location = `${pathname}${search !== null && search.length > 0 ? `?${search}` : ''}`;
    const { next: nextUrl = null } = queryString.parse(search) || {};

    const routes = useRoutes();
    const routeMatcher = useRouteMatcher();
    const isSamePage = (currentUrl: string, newUrl: string) =>
        getPathname(currentUrl) === getPathname(newUrl) ||
        getSpecificPart(currentUrl) === getSpecificPart(newUrl) ||
        samePageRoutes.some((route) => {
            const [currentMatch = false] = routeMatcher(routes[route], getPathname(currentUrl));
            const [newMatch = false] = routeMatcher(routes[route], getPathname(newUrl));
            return currentMatch && newMatch;
        });

    const [state, dispatchNow] = useReducer(timelineReducer, location, createTimelineState);
    // Timeline updates render every mounted page: they run as transitions so the browser can
    // paint the response to the interaction first.
    const dispatch = (action: TimelineAction) => startTransition(() => dispatchNow(action));
    const { entries, index, loadNext } = state;
    const current = getCurrentEntry(state);
    const lastEntry = entries[entries.length - 1];
    const withNextPlaceholder = hasNextPlaceholder(state, withoutNext);

    // Id of the current entry, read when a page navigates (initialized for the first render,
    // when page effects run before this component's effects)
    const currentIdRef = useRef<number | null>(current !== null ? current.id : null);
    useEffect(() => {
        currentIdRef.current = current !== null ? current.id : null;
    });

    // The browser location is followed by the timeline
    useEffect(() => {
        dispatch({
            type: 'locationChanged',
            url: location,
            nextUrl: typeof nextUrl === 'string' ? nextUrl : null,
            isSamePage,
            samePage: typeof window !== 'undefined' && isSamePageHistoryState(window.history.state),
        });
    }, [location]);

    // Load the next page after a delay on each page
    const isFirstPage = entries.length === 1;
    useEffect(() => {
        if (loadNext) {
            return () => {};
        }
        const timeout = setTimeout(
            () => dispatch({ type: 'loadNext' }),
            isFirstPage ? firstLoadNextDelay : loadNextDelay,
        );
        return () => clearTimeout(timeout);
    }, [loadNext, isFirstPage, firstLoadNextDelay, loadNextDelay]);

    const baseUri = useRequestBase();
    const documentUrl = useDocumentUrlGenerator();
    const { next: nextDocument = null, recommendationId = null } = useTimelineNext(
        `${baseUri}${lastEntry.url}`,
        {
            snippet: true,
        },
        {
            enabled: loadNext && withNextPlaceholder,
        },
    );
    const nextDocumentUrl =
        nextDocument !== null
            ? documentUrl(nextDocument, getTrackingQuery({ recommendationId }))
            : null;

    useEffect(() => {
        if (nextDocumentUrl !== null && !withoutNext) {
            dispatch({ type: 'nextLoaded', url: nextDocumentUrl });
        }
    }, [nextDocumentUrl, withoutNext]);

    // Pages rendered in the timeline, with `null` for the placeholder of the next page
    const timeline: (TimelineEntry | null)[] = withNextPlaceholder ? [...entries, null] : entries;

    return {
        location,
        timeline,
        index,
        currentUrl: current !== null ? current.url : null,
        nextLoaded: nextDocument !== null,
        isSamePage,
        // Moves the timeline from a gesture (drag release, click on a neighbour). The history
        // entry is written right away, while the gesture is handled: browsers skip, on back, the
        // entries added without a user interaction, and on touch screens a drag only counts as
        // one while its events are handled. The timeline follows in a transition.
        goTo: (newIndex: number) => {
            const target = entries[newIndex] || null;
            if (target !== null && newIndex !== index && target.url !== getBrowserUrl()) {
                browserNavigate(target.url);
            }
            dispatch({ type: 'goTo', index: newIndex });
        },
        navigateNext: () => dispatch({ type: 'navigateNext' }),
        // Navigation from inside a page: the current page moves the browser history (and the
        // timeline follows), another page only changes its own location.
        navigateFromPage: (
            id: number | null,
            to: string,
            options?: { replace?: boolean; state?: unknown },
        ) => {
            if (id === null || id === currentIdRef.current) {
                browserNavigate(to, options);
            } else {
                dispatch({ type: 'pageNavigated', id, url: to });
            }
        },
    };
}

interface UseTimelineHistoryOptions {
    url: string | null;
    location: string;
    settled: boolean;
}

// Once the transition of the timeline is settled, writes its current page in the browser
// history if it is not there yet (goTo writes it during the gesture; a next page that loads
// while the user is on its placeholder is written here). A page left before its transition
// settles is reported as skipped.
export function useTimelineHistory({ url, location, settled }: UseTimelineHistoryOptions) {
    const [, setLocation] = useLocation();
    const baseUri = useRequestBase();
    const { skip } = useSkipTimeline();
    const pendingRef = useRef<string | null>(null);

    useEffect(() => {
        const { current: pending } = pendingRef;
        if (pending !== null && pending !== url) {
            skip(`${baseUri}${pending}`);
        }
        pendingRef.current = url;
    }, [url]);

    useEffect(() => {
        if (!settled || url === null || pendingRef.current !== url) {
            return;
        }
        pendingRef.current = null;
        if (url !== location) {
            setLocation(url);
        }
    }, [settled, url]);
}

export default useTimelineNavigation;
